/**
 * Product carousel.
 *
 * Built on native scroll-snap rather than on transformed slides, so the track
 * keeps momentum scrolling, trackpad gestures, keyboard scrolling and the
 * browser's own accessibility behaviour. The module adds arrows, a progress
 * rule and pointer dragging on top of that; with the module absent the track
 * is still a scrollable, snapping list.
 *
 * Dragging uses a spring so releasing mid-gesture settles rather than stops
 * dead, which is what makes it feel fluid instead of mechanical.
 */
import { animate, springs, prefersReducedMotion } from 'theme:motion';

class Carousel {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-carousel-track]');
    this.prev = root.querySelector('[data-carousel-prev]');
    this.next = root.querySelector('[data-carousel-next]');
    this.progress = root.querySelector('[data-carousel-progress]');

    if (!this.track) return;

    this.items = [...this.track.children];
    if (this.items.length < 2) {
      this.root.setAttribute('data-carousel-static', '');
      return;
    }

    this.bindControls();
    this.bindDrag();
    this.bindProgress();
    this.update();
  }

  /** How far one press of an arrow moves: a whole card plus its gap. */
  get step() {
    const first = this.items[0];
    const second = this.items[1];
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }

  get maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  bindControls() {
    this.prev?.addEventListener('click', () => this.scrollBy(-1));
    this.next?.addEventListener('click', () => this.scrollBy(1));
  }

  scrollBy(direction) {
    this.track.scrollBy({
      left: this.step * direction,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  /**
   * Pointer dragging.
   *
   * Only engages once the pointer has actually moved horizontally, so a tap
   * on a card still opens the product and a vertical swipe still scrolls the
   * page. Without that threshold a carousel swallows both.
   */
  bindDrag() {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let engaged = false;
    let lastX = 0;
    let velocity = 0;

    const onDown = (event) => {
      if (event.pointerType === 'touch') return; // native touch scrolling is better
      dragging = true;
      engaged = false;
      startX = event.clientX;
      lastX = event.clientX;
      startScroll = this.track.scrollLeft;
    };

    const onMove = (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;

      if (!engaged) {
        if (Math.abs(delta) < 6) return;
        engaged = true;
        this.root.setAttribute('data-dragging', '');
        this.track.style.scrollSnapType = 'none';
      }

      velocity = event.clientX - lastX;
      lastX = event.clientX;
      this.track.scrollLeft = startScroll - delta;
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      this.root.removeAttribute('data-dragging');
      if (!engaged) return;

      // Carry the gesture's momentum into a spring so the track settles.
      const target = Math.max(
        0,
        Math.min(this.maxScroll, this.track.scrollLeft - velocity * 12)
      );

      const settle = () => {
        this.track.style.scrollSnapType = '';
      };

      if (prefersReducedMotion()) {
        this.track.scrollLeft = target;
        settle();
        return;
      }

      animate(this.track, { scrollLeft: target }, { type: springs.gentle() })
        .finished.then(settle)
        .catch(settle);
    };

    this.track.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    // A drag that ends on a card must not also count as a click on it.
    this.track.addEventListener(
      'click',
      (event) => {
        if (this.root.hasAttribute('data-dragging')) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  }

  bindProgress() {
    let ticking = false;
    this.track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          this.update();
          ticking = false;
        });
      },
      { passive: true }
    );

    new ResizeObserver(() => this.update()).observe(this.track);
  }

  update() {
    const max = this.maxScroll;
    const ratio = max > 0 ? this.track.scrollLeft / max : 0;

    this.prev?.toggleAttribute('disabled', this.track.scrollLeft <= 1);
    this.next?.toggleAttribute('disabled', this.track.scrollLeft >= max - 1);
    this.root.toggleAttribute('data-carousel-static', max <= 1);

    if (this.progress) {
      // The bar's width states how much of the run is visible, its position
      // states where you are: one element answering both questions.
      const visible = this.track.clientWidth / this.track.scrollWidth;
      this.progress.style.setProperty('--carousel-visible', String(visible));
      this.progress.style.setProperty('--carousel-progress', String(ratio));
    }
  }
}

document.querySelectorAll('[data-carousel]').forEach((root) => new Carousel(root));

/**
 * Shelves that arrive after load, such as related products and recently
 * viewed, announce themselves so they get the same behaviour as the ones
 * present at first paint.
 */
document.addEventListener('theme:carousel:refresh', (event) => {
  const root = event.detail?.root;
  if (!root) return;
  if (root.matches('[data-carousel]')) new Carousel(root);
  root.querySelectorAll('[data-carousel]').forEach((node) => new Carousel(node));
});
